// src/lib/exportAppointments.ts

import * as Sentry from '@sentry/react';
import type { AppointmentColumn } from '@/components/types/appointmentColumns';
import type { RawAppointment } from '@/components/types/typeCalendar';

// ============================================================
// UTILIDADES
// ============================================================

const normalizeValue = (value: string | number | null | undefined) =>
  value == null ? '' : value;

// ============================================================
// EXPORT EXCEL (DINÁMICO) - EXCELJS
// ============================================================

export async function exportAppointmentsToExcelDynamic(
  appointments: RawAppointment[],
  columns: AppointmentColumn[],
  fileName = 'citas.xlsx'
) {
  try {
    const ExcelJS = (await import('exceljs')).default;

    const activeColumns = columns.filter((c) => c.enabled);

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Bookoll';
    wb.created = new Date();

    const ws = wb.addWorksheet('Citas', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });

    ws.columns = activeColumns.map((c) => ({
      header: c.label,
      key: c.label,
      width: Math.min(Math.max(c.label.length + 6, 12), 45),
    }));

    const header = ws.getRow(1);
    header.height = 20;

    header.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: true,
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' },
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      };
    });

    appointments.forEach((appt, idx) => {
      const values = activeColumns.map((c) =>
        normalizeValue(c.exportValue(appt))
      );

      const row = ws.addRow(values);
      row.height = 18;

      const zebra = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF9FAFB';

      row.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
          wrapText: true,
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: zebra },
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFF3F4F6' } },
          left: { style: 'thin', color: { argb: 'FFF3F4F6' } },
          bottom: { style: 'thin', color: { argb: 'FFF3F4F6' } },
          right: { style: 'thin', color: { argb: 'FFF3F4F6' } },
        };
      });
    });

    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: activeColumns.length },
    };

    activeColumns.forEach((c, i) => {
      const colIdx = i + 1;
      let maxLen = c.label.length;

      ws.getColumn(colIdx).eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value;
        const s = v == null ? '' : String(v);
        if (s.length > maxLen) maxLen = s.length;
      });

      ws.getColumn(colIdx).width = Math.min(Math.max(maxLen + 2, 12), 45);
    });

    const buf = await wb.xlsx.writeBuffer();

    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { fileName, appointmentsCount: appointments.length },
    });
    throw err;
  }
}

// ============================================================
// EXPORT PDF (DINÁMICO)
// ============================================================

export async function exportAppointmentsToPDFDynamic(
  appointments: RawAppointment[],
  columns: AppointmentColumn[],
  fileName = 'citas.pdf'
) {
  try {
    const jsPDF = (await import('jspdf')).default;
    const autoTable = (await import('jspdf-autotable')).default;

    const doc = new jsPDF();

    const activeColumns = columns.filter((c) => c.enabled);
    const headers = activeColumns.map((c) => c.label);
    const body = appointments.map((appt) =>
      activeColumns.map((c) => normalizeValue(c.exportValue(appt)))
    );

    autoTable(doc, {
      head: [headers],
      body,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [16, 50, 84],
        textColor: 255,
        fontStyle: 'bold',
      },
      margin: { top: 20 },
    });

    doc.save(fileName);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { fileName, appointmentsCount: appointments.length },
    });
    throw err;
  }
}
